import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, sendSignInLinkToEmail, signInWithPopup } from 'firebase/auth';
import { collection, deleteDoc, doc, getDoc, getDocs, getFirestore, increment, orderBy, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: `${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || `${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.appspot.com`,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const googleProvider = new GoogleAuthProvider();

export const handleGoogleSignIn = async () => {
  const result = await signInWithPopup(auth, googleProvider);
  const token = await result.user.getIdToken();
  return token;
};

export const handleEmailSignIn = async (email) => {
  const actionCodeSettings = {
    url: `${process.env.NEXT_PUBLIC_APP_URL}/login?finishSignUp=true&email=${email}`,
    handleCodeInApp: true,
  };
  const resp = await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  return resp;
};

export const updateUserProfile = async (userId, data) => {
  try {
    const userRef = doc(db, 'userProfiles', userId);
    await setDoc(userRef, data, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating user profile:', error);
    return false;
  }
};

export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'userProfiles', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data();
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

// Favorites functionality
export const addDeckToFavorites = async (userId, deckId, deckData) => {
  try {
    // Create a document in the userFavorites collection
    const favoriteRef = doc(db, 'userFavorites', userId, 'favoriteDecks', deckId);
    await setDoc(favoriteRef, {
      addedAt: serverTimestamp(),
      deckId,
      deckName: deckData.name,
      deckFormat: deckData.format,
      authorId: deckData.userId,
      authorName: deckData.createdBy || 'Anon',
    });

    // Also update the deck document with a counter
    const deckRef = doc(db, 'decks', deckId);
    const deckSnap = await getDoc(deckRef);

    if (deckSnap.exists()) {
      // Increment the favoritesCount field if it exists, or set it to 1 if it doesn't
      const currentCount = deckSnap.data().favoritesCount || 0;
      await updateDoc(deckRef, {
        favoritesCount: currentCount + 1,
      });
    }

    return true;
  } catch (error) {
    console.error('Error adding deck to favorites:', error);
    return false;
  }
};

export const removeDeckFromFavorites = async (userId, deckId) => {
  try {
    const favoriteRef = doc(db, 'userFavorites', userId, 'favoriteDecks', deckId);
    await deleteDoc(favoriteRef);

    // Also update the deck document with a counter
    const deckRef = doc(db, 'decks', deckId);
    const deckSnap = await getDoc(deckRef);

    if (deckSnap.exists()) {
      // Decrement the favoritesCount field, ensuring it doesn't go below 0
      const currentCount = deckSnap.data().favoritesCount || 0;
      await updateDoc(deckRef, {
        favoritesCount: Math.max(0, currentCount - 1),
      });
    }

    return true;
  } catch (error) {
    console.error('Error removing deck from favorites:', error);
    return false;
  }
};

export const checkIfDeckIsFavorite = async (userId, deckId) => {
  try {
    const favoriteRef = doc(db, 'userFavorites', userId, 'favoriteDecks', deckId);
    const favoriteSnap = await getDoc(favoriteRef);
    return favoriteSnap.exists();
  } catch (error) {
    console.error('Error checking if deck is favorite:', error);
    return false;
  }
};

export const getUserFavoriteDecks = async (userId) => {
  try {
    const favoritesRef = collection(db, 'userFavorites', userId, 'favoriteDecks');
    const favoritesQuery = query(favoritesRef, orderBy('addedAt', 'desc'));
    const querySnapshot = await getDocs(favoritesQuery);

    const favoriteDecks = [];
    querySnapshot.forEach((doc) => {
      favoriteDecks.push({
        id: doc.id,
        ...doc.data(),
        addedAt: doc.data().addedAt?.toDate() || new Date(),
      });
    });

    return favoriteDecks;
  } catch (error) {
    console.error('Error getting user favorite decks:', error);
    return [];
  }
};

// Get the total favorites count for a deck
export const getDeckFavoritesCount = async (deckId) => {
  try {
    // Get the deck document which has the count stored directly
    const deckRef = doc(db, 'decks', deckId);
    const deckSnap = await getDoc(deckRef);

    if (deckSnap.exists()) {
      // Return the favoritesCount field or 0 if it doesn't exist
      return deckSnap.data().favoritesCount || 0;
    }

    return 0;
  } catch (error) {
    console.error('Error counting deck favorites:', error);
    return 0;
  }
};

// Feedback functions
export const getFeedbackList = async () => {
  try {
    const feedbackRef = collection(db, 'feedback');
    const q = query(feedbackRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const feedbackList = [];
    querySnapshot.forEach((doc) => {
      feedbackList.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      });
    });

    return feedbackList;
  } catch (error) {
    console.error('Error getting feedback list:', error);
    return [];
  }
};

export const updateFeedbackStatus = async (feedbackId, status) => {
  try {
    const feedbackRef = doc(db, 'feedback', feedbackId);
    await updateDoc(feedbackRef, {
      status,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error updating feedback status:', error);
    return false;
  }
};

// Collection functionality
export const addCardToCollection = async (userId, card, quantity = 1, category = 'mtg', condition = 'NM', language = 'English', finish = 'Non-Foil', purchasePrice, isProxy = false) => {
  try {
    // Validate inputs
    if (!userId) {
      console.error('addCardToCollection: Missing userId');
      return { success: false, error: 'Missing userId' };
    }

    if (!card || !card.id) {
      console.error('addCardToCollection: Invalid card object', card);
      return { success: false, error: 'Invalid card object' };
    }

    console.log('Adding card to collection:', {
      userId,
      cardId: card.id,
      cardName: card.name,
      quantity,
      category,
      condition,
      language,
      finish,
      isProxy,
    });

    // Check if we have any cards with the same base card ID in the collection
    const collectionRef = collection(db, 'userCollections', userId, category);
    const q = query(collectionRef, where('cardId', '==', card.id));
    const querySnapshot = await getDocs(q);

    // Check if there's a card with the same properties
    let existingCardWithSameProps = null;

    querySnapshot.forEach((doc) => {
      const cardData = doc.data();
      if (
        cardData.condition === condition &&
        cardData.language === language &&
        cardData.finish === finish &&
        cardData.isProxy === isProxy &&
        // For purchasePrice, we need to handle undefined/null cases
        ((purchasePrice === undefined && (cardData.purchasePrice === undefined || cardData.purchasePrice === null)) || (purchasePrice !== undefined && cardData.purchasePrice === purchasePrice))
      ) {
        existingCardWithSameProps = { id: doc.id, ...cardData };
      }
    });

    if (existingCardWithSameProps) {
      // If a card with the same properties exists, update its quantity
      console.log('Card with same properties exists, updating quantity...', existingCardWithSameProps);

      const cardRef = doc(db, 'userCollections', userId, category, existingCardWithSameProps.id);
      await updateDoc(cardRef, {
        quantity: increment(quantity),
        updatedAt: serverTimestamp(),
      });

      console.log('Card quantity updated successfully');
      return { success: true, cardId: existingCardWithSameProps.id };
    } else {
      // Create a new document with a unique ID
      console.log('Creating new card entry with unique properties...');

      // Generate a unique ID that includes the card ID and properties
      const uniqueId = `${card.id}_${Date.now()}`;
      const cardRef = doc(db, 'userCollections', userId, category, uniqueId);

      // Create document data
      const docData = {
        cardId: card.id,
        cardName: card.name,
        setName: card.set_name,
        setCode: card.set,
        rarity: card.rarity,
        imageUri: card.image_uris?.normal || (card.card_faces && card.card_faces[0]?.image_uris?.normal) || card.image_uris?.large,
        price: card.prices?.usd || '0',
        quantity,
        condition,
        language,
        finish,
        // Only include purchasePrice if it's not undefined
        ...(purchasePrice !== undefined && { purchasePrice }),
        isProxy,
        addedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        card: card, // Store the full card object for reference
      };

      console.log('Setting document with data:', docData);
      await setDoc(cardRef, docData);
      console.log('Card added successfully');

      return { success: true, cardId: uniqueId };
    }
  } catch (error) {
    console.error('Error adding card to collection:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
};

export const removeCardFromCollection = async (userId, cardId, quantity = 1, category = 'mtg') => {
  try {
    const cardRef = doc(db, 'userCollections', userId, category, cardId);
    const cardSnap = await getDoc(cardRef);

    if (cardSnap.exists()) {
      const currentQuantity = cardSnap.data().quantity || 0;

      if (currentQuantity <= quantity) {
        // If removing all or more than we have, delete the document
        await deleteDoc(cardRef);
        return { success: true, action: 'deleted' };
      } else {
        // Otherwise, decrement the quantity
        await updateDoc(cardRef, {
          quantity: increment(-quantity),
          updatedAt: serverTimestamp(),
        });
        return { success: true, action: 'updated', remainingQuantity: currentQuantity - quantity };
      }
    }

    return { success: false, error: 'Card not found in collection' };
  } catch (error) {
    console.error('Error removing card from collection:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
};

export const getUserCollection = async (userId, category = 'mtg') => {
  try {
    const collectionRef = collection(db, 'userCollections', userId, category);
    const collectionQuery = query(collectionRef, orderBy('addedAt', 'desc'));
    const querySnapshot = await getDocs(collectionQuery);

    const collectionCards = [];
    querySnapshot.forEach((doc) => {
      collectionCards.push({
        id: doc.id,
        ...doc.data(),
        addedAt: doc.data().addedAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      });
    });

    return collectionCards;
  } catch (error) {
    console.error('Error getting user collection:', error);
    return [];
  }
};

export const getCollectionValue = async (userId, category = 'mtg') => {
  try {
    const collectionRef = collection(db, 'userCollections', userId, category);
    const querySnapshot = await getDocs(collectionRef);

    let totalValue = 0;
    querySnapshot.forEach((doc) => {
      const card = doc.data();
      const price = parseFloat(card.price) || 0;
      const quantity = card.quantity || 0;
      totalValue += price * quantity;
    });

    return totalValue.toFixed(2);
  } catch (error) {
    console.error('Error calculating collection value:', error);
    return '0.00';
  }
};

export const clearUserCollection = async (userId, category = 'mtg') => {
  try {
    // Get all cards in the collection
    const collectionRef = collection(db, 'userCollections', userId, category);
    const querySnapshot = await getDocs(collectionRef);

    // Use a batch to delete all documents
    const batch = writeBatch(db);

    querySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    // Commit the batch
    await batch.commit();

    console.log(`Cleared collection for user ${userId} in category ${category}`);
    return true;
  } catch (error) {
    console.error('Error clearing collection:', error);
    return false;
  }
};

export const bulkAddCardsToCollection = async (userId, cards, category = 'mtg') => {
  try {
    // Use a batch write for better performance with multiple cards
    const batch = writeBatch(db);
    const processedCards = [];

    for (const cardItem of cards) {
      const card = cardItem.details;
      const quantity = cardItem.quantity || 1;
      const condition = cardItem.condition || 'NM';
      const language = cardItem.language || 'English';
      const finish = cardItem.finish || 'Non-Foil';
      const purchasePrice = cardItem.purchasePrice;
      const isProxy = cardItem.isProxy || false;

      if (!card || !card.id) continue;

      // Check if we have any cards with the same base card ID in the collection
      const collectionRef = collection(db, 'userCollections', userId, category);
      const q = query(collectionRef, where('cardId', '==', card.id));
      const querySnapshot = await getDocs(q);

      // Check if there's a card with the same properties
      let existingCardWithSameProps = null;

      querySnapshot.forEach((doc) => {
        const cardData = doc.data();
        if (
          cardData.condition === condition &&
          cardData.language === language &&
          cardData.finish === finish &&
          cardData.isProxy === isProxy &&
          // For purchasePrice, we need to handle undefined/null cases
          ((purchasePrice === undefined && (cardData.purchasePrice === undefined || cardData.purchasePrice === null)) || (purchasePrice !== undefined && cardData.purchasePrice === purchasePrice))
        ) {
          existingCardWithSameProps = { id: doc.id, ...cardData };
        }
      });

      if (existingCardWithSameProps) {
        // If a card with the same properties exists, update its quantity
        console.log('Card with same properties exists, updating quantity...', existingCardWithSameProps);

        const cardRef = doc(db, 'userCollections', userId, category, existingCardWithSameProps.id);
        batch.update(cardRef, {
          quantity: increment(quantity),
          updatedAt: serverTimestamp(),
        });

        processedCards.push({
          id: existingCardWithSameProps.id,
          action: 'updated',
        });
      } else {
        // Create a new document with a unique ID
        console.log('Creating new card entry with unique properties...');

        // Generate a unique ID that includes the card ID and properties
        const uniqueId = `${card.id}_${Date.now()}_${processedCards.length}`;
        const cardRef = doc(db, 'userCollections', userId, category, uniqueId);

        // Create document data
        const docData = {
          cardId: card.id,
          cardName: card.name,
          setName: card.set_name,
          setCode: card.set,
          rarity: card.rarity,
          imageUri: card.image_uris?.normal || (card.card_faces && card.card_faces[0]?.image_uris?.normal),
          price: card.prices?.usd || '0',
          quantity,
          condition,
          language,
          finish,
          // Only include purchasePrice if it's not undefined
          ...(purchasePrice !== undefined && { purchasePrice }),
          isProxy,
          addedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          card: card, // Store the full card object for reference
        };

        batch.set(cardRef, docData);

        processedCards.push({
          id: uniqueId,
          action: 'created',
        });
      }
    }

    await batch.commit();
    return { success: true, processedCards };
  } catch (error) {
    console.error('Error bulk adding cards to collection:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
};

// Generated Images functionality
export const uploadGeneratedImage = async (userId, imageBytes, cardId) => {
  try {
    if (!userId || !imageBytes) {
      console.error('uploadGeneratedImage: Missing required parameters');
      return { success: false, error: 'Missing required parameters' };
    }

    // Create a unique filename
    const imagePath = `generated-images/${userId}/${cardId}_${Date.now()}.png`;
    console.log(`Attempting to upload to path: ${imagePath}`);

    // Log storage bucket information for debugging
    console.log(`Storage bucket: ${storage.app.options.storageBucket}`);

    const storageRef = ref(storage, imagePath);

    // Upload with metadata
    const metadata = {
      contentType: 'image/png',
      customMetadata: {
        userId: userId,
        cardId: cardId,
      },
    };

    const uploadResult = await uploadBytes(storageRef, imageBytes, metadata);
    console.log('Upload successful:', uploadResult);

    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);
    console.log('Download URL obtained:', downloadURL);

    return {
      success: true,
      storagePath: imagePath,
      downloadURL,
    };
  } catch (error) {
    console.error('Error uploading generated image:', error);
    // Provide more detailed error information
    const errorDetails = {
      code: error.code,
      message: error.message,
      serverResponse: error.customData?.serverResponse || '',
      status: error.status_ || '',
      storageBucket: storage.app.options.storageBucket || '',
    };
    console.error('Error details:', errorDetails);

    return {
      success: false,
      error: error.message || 'Unknown error',
      errorDetails,
    };
  }
};

export const saveGeneratedImage = async (userId, imageData, cardData, customPrompt, tcgType = 'mtg') => {
  try {
    if (!userId || !imageData || !cardData) {
      console.error('saveGeneratedImage: Missing required parameters');
      return { success: false, error: 'Missing required parameters' };
    }

    // Create a document in the generatedImages collection
    const generatedImageRef = doc(collection(db, 'generatedImages'));

    // include only fields with a property
    const cleanCardData = Object.fromEntries(Object.entries(cardData).filter(([key, value]) => value !== null && value !== undefined));

    await setDoc(generatedImageRef, {
      userId,
      imageStoragePath: imageData.storagePath,
      imageUrl: imageData.downloadURL,
      customPrompt,
      createdAt: serverTimestamp(),
      tcgType,
      originalCardData: cleanCardData, // Store the full card object for reference
    });

    return {
      success: true,
      generatedImageId: generatedImageRef.id,
    };
  } catch (error) {
    console.error('Error saving generated image data:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
};

export const getUserGeneratedImages = async (userId, tcgType = null) => {
  try {
    let generatedImagesQuery;
    const generatedImagesRef = collection(db, 'generatedImages');

    if (tcgType) {
      // Filter by user ID and TCG type
      generatedImagesQuery = query(generatedImagesRef, where('userId', '==', userId), where('tcgType', '==', tcgType), orderBy('createdAt', 'desc'));
    } else {
      // Filter by user ID only
      generatedImagesQuery = query(generatedImagesRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));
    }

    const querySnapshot = await getDocs(generatedImagesQuery);

    const generatedImages = [];
    querySnapshot.forEach((doc) => {
      generatedImages.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      });
    });

    return generatedImages;
  } catch (error) {
    console.error('Error getting user generated images:', error);
    return [];
  }
};
